/**
 * Command Palette
 *
 * Quick navigation and search with Cmd+K trigger.
 * Uses lucide-react icons, recent items tracking, and expanded command set.
 */

import React, { useState, useEffect, useMemo, Fragment, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, Combobox, Transition } from '@headlessui/react';
import {
  Search, Home, Inbox, Library, BarChart3, Settings, ChevronRight,
  List, FileText, Clock, PlusCircle, RefreshCcw,
  Target, Wrench, Scale, Layers, Star, Filter, Sparkles, Compass,
} from 'lucide-react';
import { useDegapProcessStore } from '../../stores/prioritization/degapProcessStore';
import { useItemPoolsStore } from '../../stores/prioritization/itemPoolsStore';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { UI_LABELS } from '../../lib/terminology';
import { useTheme } from '../../contexts/ThemeContext';
import { useItemListPqlCatalog } from './item-list/hooks/useItemListPqlCatalog';
import { printQuery, safeParse, toEnglish } from '../../lib/pql';
import { translateEnglishToPql } from '../../lib/pql/ai/nlToPql';
import { isAiCapabilityEnabled } from '../../lib/ai/aiAssistSettings';
import { useAiControl } from '../../lib/ai/aiAvailability';
import { AskAiNeedsKey } from '../shared/AskAiNeedsKey';
import { useOptionalHarryShell } from '../harry/harryShellContext';
import { useItemOutcomes } from '../../hooks/prioritization/useItemOutcomes';
import { outcomeHeadline } from '../../lib/items/itemOutcomes';
import { useAltitudeText } from '../../hooks/prioritization/useAltitudeText';
import { useKeyboardShortcutHints } from '../../hooks/useKeyboardShortcutHints';

type CommandType = 'navigation' | 'cycle' | 'list' | 'tool' | 'action' | 'item' | 'recent' | 'query';

/** The item list reads its query from this param; the bar renders it, editable, with a live count. */
const itemListQueryHref = (statement: string) =>
  `/prioritization/items?pql=${encodeURIComponent(statement)}`;

interface Command {
  id: string;
  name: string;
  description?: string;
  type: CommandType;
  icon?: React.ComponentType<{ className?: string }>;
  href?: string;
  action?: () => void;
  keywords?: string[];
}

const RECENT_KEY = 'pp_recent_items';

function getRecentItems(): { id: string; title: string; href: string }[] {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); } catch { return []; }
}

// eslint-disable-next-line react-refresh/only-export-components
export function addRecentItem(item: { id: string; title: string; href: string }) {
  try {
    const existing = getRecentItems().filter(r => r.id !== item.id);
    localStorage.setItem(RECENT_KEY, JSON.stringify([item, ...existing].slice(0, 6)));
  } catch {
    // ignore storage failures
  }
}

const navigationCommands: Command[] = [
  { id: 'nav-dashboard', name: 'Home', type: 'navigation', icon: Home, href: '/prioritization', keywords: ['dashboard', 'overview'] },
  { id: 'nav-submit', name: 'Submit an idea', type: 'navigation', icon: PlusCircle, href: '/prioritization/intake/submit', keywords: ['submit', 'idea', 'new item', 'intake'] },
  { id: 'nav-triage', name: 'Triage Queue', type: 'navigation', icon: Inbox, href: '/prioritization/intake/triage', keywords: ['triage', 'queue', 'review'] },
  { id: 'nav-items', name: 'All Items', type: 'navigation', icon: Library, href: '/prioritization/items', keywords: ['items', 'library', 'all'] },
  { id: 'nav-pipeline-health-report', name: 'Pipeline Health', type: 'navigation', icon: FileText, href: '/prioritization/reports/pipeline-health', keywords: ['pipeline', 'backlog', 'readiness', 'funnel', 'stale', 'report'] },
  { id: 'nav-scoring', name: 'Values / Scoring', type: 'navigation', icon: Scale, href: '/prioritization/scoring', keywords: ['scoring', 'values', 'criteria'] },
  { id: 'nav-clusters', name: 'Clusters', type: 'navigation', icon: Layers, href: '/prioritization/shape/clusters', keywords: ['clusters', 'groups', 'themes'] },
  { id: 'nav-cycles', name: 'Cycles & Tracks', type: 'navigation', icon: RefreshCcw, href: '/prioritization/cycles', keywords: ['cycles', 'tracks', 'comparison frames', 'governance'] },
  { id: 'nav-sessions', name: 'Sessions', type: 'navigation', icon: Target, href: '/prioritization/sessions', keywords: ['sessions', 'prioritization meeting'] },
  { id: 'nav-lists', name: UI_LABELS.itemPools, type: 'navigation', icon: List, href: '/prioritization/lists', keywords: ['lists', 'pools', 'focus'] },
  { id: 'nav-monitor', name: 'Monitor', type: 'navigation', icon: BarChart3, href: '/prioritization/monitor', keywords: ['monitor', 'drift', 'health'] },
  { id: 'nav-tasks', name: 'My work', type: 'navigation', icon: Clock, href: '/prioritization/my-work', keywords: ['tasks', 'my work', 'pending'] },
  { id: 'nav-tools', name: 'Tools', type: 'navigation', icon: Wrench, href: '/prioritization/tools', keywords: ['tools', 'methods', 'frameworks'] },
  { id: 'nav-settings', name: 'Settings', type: 'navigation', icon: Settings, href: '/prioritization/settings', keywords: ['settings', 'admin', 'config'] },
];

const toolCommands: Command[] = [
  { id: 'tool-rice', name: 'RICE Scoring', type: 'tool', icon: BarChart3, href: '/prioritization/tools/rice-scoring', keywords: ['reach', 'impact', 'confidence', 'effort'] },
  { id: 'tool-wsjf', name: 'WSJF', type: 'tool', icon: BarChart3, href: '/prioritization/tools/wsjf', keywords: ['weighted', 'shortest', 'job', 'first'] },
  { id: 'tool-eisenhower', name: 'Eisenhower Matrix', type: 'tool', icon: BarChart3, href: '/prioritization/tools/eisenhower-matrix', keywords: ['urgent', 'important', 'matrix'] },
  { id: 'tool-moscow', name: 'MoSCoW Method', type: 'tool', icon: BarChart3, href: '/prioritization/tools/moscow-method', keywords: ['must', 'should', 'could', 'wont'] },
  { id: 'tool-pairwise', name: 'Pairwise Ranking', type: 'tool', icon: BarChart3, href: '/prioritization/tools/pairwise-ranking', keywords: ['compare', 'versus'] },
  { id: 'tool-impact', name: 'Impact/Effort Matrix', type: 'tool', icon: BarChart3, href: '/prioritization/tools/impact-effort-matrix', keywords: ['quick wins', 'big bets'] },
  { id: 'tool-dot', name: 'Dot Voting', type: 'tool', icon: BarChart3, href: '/prioritization/tools/dot-voting', keywords: ['vote', 'democracy'] },
  { id: 'tool-stack', name: 'Stack Ranking', type: 'tool', icon: BarChart3, href: '/prioritization/tools/stack-ranking', keywords: ['forced', 'ranking'] },
  { id: 'tool-buy', name: 'Buy a Feature', type: 'tool', icon: Star, href: '/prioritization/tools/buy-a-feature', keywords: ['buy', 'feature', 'budget'] },
  { id: 'tool-pareto', name: 'Pareto Analysis', type: 'tool', icon: BarChart3, href: '/prioritization/tools/pareto-analysis', keywords: ['pareto', '80/20', 'vital few'] },
  { id: 'tool-cod', name: 'Cost of Delay', type: 'tool', icon: Clock, href: '/prioritization/tools/cost-of-delay', keywords: ['cost', 'delay', 'urgency'] },
  { id: 'tool-speedboat', name: 'Speed Boat', type: 'tool', icon: Target, href: '/prioritization/tools/speed-boat', keywords: ['speed', 'boat', 'anchors', 'innovation game'] },
  { id: 'tool-prune', name: 'Prune the Product Tree', type: 'tool', icon: Layers, href: '/prioritization/tools/prune-product-tree', keywords: ['prune', 'tree', 'features', 'innovation game'] },
  { id: 'tool-weighted', name: 'Simple Weighted Scoring', type: 'tool', icon: Scale, href: '/prioritization/tools/simple-weighted-scoring', keywords: ['weighted', 'scoring', 'criteria'] },
  { id: 'tool-pyramid', name: 'Max Priorities Pyramid', type: 'tool', icon: Layers, href: '/prioritization/tools/max-priorities-pyramid', keywords: ['pyramid', 'max', 'limit', 'focus'] },
  { id: 'tool-fast-right', name: 'Fast vs. Right', type: 'tool', icon: Wrench, href: '/prioritization/tools/fast-vs-right', keywords: ['fast', 'right', 'tradeoff', 'speed quality'] },
  { id: 'tool-magic', name: 'Magic Prioritization', type: 'tool', icon: Star, href: '/prioritization/tools/magic-prioritization', keywords: ['magic', 'ai', 'automatic'] },
];

const actionCommands: Command[] = [
  { id: 'action-new-item', name: 'Submit an idea', type: 'action', icon: PlusCircle, href: '/prioritization/intake/submit', keywords: ['add', 'submit', 'create', 'new'] },
  { id: 'action-new-cycle', name: 'Create New Cycle', type: 'action', icon: RefreshCcw, href: '/prioritization/cycles/new', keywords: ['start', 'cycle'] },
  { id: 'action-my-tasks', name: 'Go to My work', type: 'action', icon: Clock, href: '/prioritization/my-work', keywords: ['tasks', 'pending'] },
  { id: 'action-notifications', name: 'Notifications', type: 'action', icon: Clock, href: '/prioritization/notifications', keywords: ['notifications', 'alerts'] },
];

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const navigatorShell = useOptionalHarryShell();
  const inputRef = useRef<HTMLInputElement>(null);
  const { isDark } = useTheme();
  const altitudeText = useAltitudeText();
  // The key legend (↵ select, ↑↓ navigate, ⌘K) is left off where there is no keyboard.
  const showShortcutHints = useKeyboardShortcutHints();

  const catalog = useItemListPqlCatalog();
  // The Ask row follows the statement bar's rule (useAiControl): drawn only
  // when a model is known to answer, never a row that fails after the click;
  // when none is, the one no-model line is drawn under the results instead.
  const { drawable: aiQueryEnabled, needsKey: aiNeedsKey } = useAiControl(
    useMemo(() => isAiCapabilityEnabled('nl_to_pql'), []),
  );
  const [askStatus, setAskStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [askMessage, setAskMessage] = useState<string | null>(null);

  const cycles = useDegapProcessStore(state => state.cycles);
  const itemPools = useItemPoolsStore(state => state.itemPools);
  const items = useItemsStore(state => state.items);

  const recentItems = useMemo((): Command[] => {
    if (query) return [];
    return getRecentItems().map(r => ({
      id: `recent-${r.id}`,
      name: r.title,
      description: 'Recently viewed',
      type: 'recent' as const,
      icon: Clock,
      href: r.href,
    }));
  }, [query]);

  const dynamicCommands = useMemo(() => {
    // Every cycle, pool and item is matched; the short lists are cut after the
    // match, never before it (Roadmap E-2, Nov 3: the first 20 items only, so
    // "Boot from Block", #1 on Backspace's plan, could not be found).
    const cycleCommands: Command[] = (cycles || []).map(c => ({
      id: `cycle-${c.id}`,
      name: c.name,
      description: c.currentPhase || c.status,
      type: 'cycle' as const,
      icon: RefreshCcw,
      href: `/prioritization/cycles/${c.id}`,
      keywords: ['cycle', c.status || ''],
    }));

    const listCommands: Command[] = (itemPools || []).map(l => ({
      id: `list-${l.id}`,
      name: l.name,
      description: `${l.priorityIds?.length ?? 0} items`,
      type: 'list' as const,
      icon: List,
      href: `/prioritization/lists/${l.id}`,
      keywords: ['list', 'pool', 'focus'],
    }));

    const itemCommands: Command[] = (items || []).map(c => ({
      id: `item-${c.id}`,
      name: c.title,
      description: c.exit_status || c.lifecycle_state,
      type: 'item' as const,
      icon: FileText,
      href: `/prioritization/items/${c.id}`,
      // Findable by its altitude the way it is read: "altitude 3", "epic" (the number first, the name a tag).
      keywords: ['item', c.itemCategory || '', c.altitude || '', altitudeText.format(c.altitudeLevel ?? c.altitude, c.itemCategory) ?? ''],
    }));

    return { cycles: cycleCommands, lists: listCommands, items: itemCommands };
  }, [cycles, itemPools, items, altitudeText]);

  useEffect(() => { if (!open) setQuery(''); }, [open]);
  useEffect(() => { setAskStatus('idle'); setAskMessage(null); }, [query, open]);

  /**
   * Query rows (PRD §6.2, the command bar). Typed text that parses as PQL can
   * be run as-is; anything else can be sent to nl_to_pql. Either way the
   * statement lands in the item list's editable statement bar via ?pql= — the
   * palette never applies a hidden filter, and only the parser decides what
   * runs (PRD §6.5 honesty rule 1).
   */
  const askInEnglish = useCallback(async (english: string) => {
    setAskStatus('loading');
    setAskMessage(null);
    const result = await translateEnglishToPql(english, catalog);
    if (result.ok && result.statement) {
      navigate(itemListQueryHref(result.statement));
      onClose();
      return;
    }
    setAskStatus('error');
    setAskMessage(result.reason ?? 'AI could not translate that.');
  }, [catalog, navigate, onClose]);

  const queryCommands = useMemo((): Command[] => {
    const text = query.trim();
    if (!text) return [];
    const rows: Command[] = [];

    const parsed = safeParse(text, catalog);
    if (parsed.ok) {
      const canonical = printQuery(parsed.query);
      rows.push({
        id: 'query-run',
        name: `Run: ${canonical}`,
        description: toEnglish(parsed.query, catalog),
        type: 'query',
        icon: Filter,
        href: itemListQueryHref(canonical),
      });
    }

    if (aiQueryEnabled) {
      rows.push({
        id: 'query-ask',
        name: `Ask: "${text}"`,
        description:
          askStatus === 'loading'
            ? 'Translating…'
            : askStatus === 'error'
              ? (askMessage ?? 'AI could not translate that.')
              : 'Translate to a PQL statement, then run',
        type: 'query',
        icon: Sparkles,
        action: () => { void askInEnglish(text); },
      });
    }
    // A sentence can go to the Navigator, which finds where it is done or where
    // to start (PRD-navigator-that-does-it FR-0). A name alone is a search.
    if (navigatorShell && text.split(/\s+/).length >= 2) {
      rows.push({
        id: 'query-navigator',
        name: `Navigator: "${text}"`,
        description: 'Find where to do this, or where to start',
        type: 'query',
        icon: Compass,
        action: () => { onClose(); navigatorShell.openWith(text); },
      });
    }
    return rows;
  }, [query, catalog, aiQueryEnabled, askStatus, askMessage, askInEnglish, navigatorShell, onClose]);

  const filteredCommands = useMemo(() => {
    const lq = query.toLowerCase().trim();
    const matches = (cmd: Command) => {
      if (!lq) return true;
      if (cmd.name.toLowerCase().includes(lq)) return true;
      if (cmd.description?.toLowerCase().includes(lq)) return true;
      if (cmd.keywords?.some(k => k.toLowerCase().includes(lq))) return true;
      return false;
    };

    if (!lq) {
      return {
        query: [] as Command[],
        recent: recentItems.slice(0, 3),
        cycles: dynamicCommands.cycles.slice(0, 3),
        lists: dynamicCommands.lists.slice(0, 2),
        items: [],
        navigation: navigationCommands.slice(0, 5),
        tools: [],
        actions: actionCommands.slice(0, 3),
      };
    }
    const found = {
      cycles: dynamicCommands.cycles.filter(matches).slice(0, 5),
      lists: dynamicCommands.lists.filter(matches).slice(0, 5),
      items: dynamicCommands.items.filter(matches).slice(0, 8),
      navigation: navigationCommands.filter(matches),
      tools: toolCommands.filter(matches),
      actions: actionCommands.filter(matches),
    };
    // When the words name nothing here, the Navigator row comes first.
    const namesNothing = Object.values(found).every((group) => group.length === 0);
    const navigatorRow = queryCommands.filter((c) => c.id === 'query-navigator');
    const otherQueryRows = queryCommands.filter((c) => c.id !== 'query-navigator');
    return {
      query: namesNothing ? [...navigatorRow, ...otherQueryRows] : [...otherQueryRows, ...navigatorRow],
      recent: [],
      ...found,
    };
  }, [query, dynamicCommands, recentItems, queryCommands]);

  // An archived item in the results says what happened to it ("Merged into …")
  // instead of the bare status word (S-27, item_outcomes mig 860).
  const archivedResultIds = useMemo(
    () => filteredCommands.items.filter((c) => c.description === 'archived').map((c) => c.id.replace('item-', '')),
    [filteredCommands.items],
  );
  const { outcomes: itemOutcomes } = useItemOutcomes(archivedResultIds);
  const itemResults = useMemo(
    () => filteredCommands.items.map((c) => {
      const o = itemOutcomes.get(c.id.replace('item-', ''));
      return o ? { ...c, description: outcomeHeadline(o) } : c;
    }),
    [filteredCommands.items, itemOutcomes],
  );

  const allResults = [
    ...filteredCommands.query,
    ...filteredCommands.recent,
    ...filteredCommands.cycles,
    ...filteredCommands.lists,
    ...itemResults,
    ...filteredCommands.navigation,
    ...filteredCommands.tools,
    ...filteredCommands.actions,
  ];

  const handleSelect = useCallback((command: Command) => {
    // The Ask row is async and may fail — it navigates and closes itself on
    // success, and keeps the palette open (with the reason) on failure.
    if (command.type === 'query' && command.action) {
      command.action();
      return;
    }
    if (command.action) {
      command.action();
    } else if (command.href) {
      if (command.type === 'item') {
        const rawId = command.id.replace('item-', '');
        addRecentItem({ id: rawId, title: command.name, href: command.href });
      }
      navigate(command.href);
    }
    onClose();
  }, [navigate, onClose]);

  return (
    <Transition.Root show={open} as={Fragment} afterLeave={() => setQuery('')}>
      <Dialog as="div" className="relative z-[100]" onClose={onClose} initialFocus={inputRef}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-[var(--bg)]/40 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto p-4 sm:p-6 md:p-20">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200" enterFrom="opacity-0 scale-95 translate-y-1" enterTo="opacity-100 scale-100 translate-y-0"
            leave="ease-in duration-150" leaveFrom="opacity-100 scale-100 translate-y-0" leaveTo="opacity-0 scale-95 translate-y-1"
          >
            <Dialog.Panel
              className="mx-auto max-w-2xl transform overflow-hidden transition-all"
              style={isDark ? {
                background: 'var(--bg-raised)',
                border: '1px solid var(--rule-bright)',
                borderRadius: '4px',
                boxShadow: '0 0 0 1px var(--rule-bright)',
              } : {
                background: '#fff',
                borderRadius: '1rem',
                boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
                border: '1px solid rgba(209,213,219,0.8)',
              }}
            >
              <Combobox onChange={handleSelect}>
                {/* Search input */}
                <div
                  className="relative"
                  style={{ borderBottom: isDark ? '1px solid var(--rule)' : '1px solid #f3f4f6' }}
                >
                  <Search
                    className="pointer-events-none absolute left-4 top-3.5 h-5 w-5"
                    style={{ color: isDark ? 'var(--text-ter)' : '#9ca3af' }}
                    aria-hidden="true"
                  />
                  <Combobox.Input
                    ref={inputRef}
                    className="no-dark h-12 w-full border-0 pl-11 pr-16 focus:ring-0 sm:text-sm font-medium outline-none"
                    style={{
                      background: 'transparent',
                      color: isDark ? 'var(--text-pri)' : '#111827',
                    }}
                    placeholder="Search commands, items, navigate…"
                    onChange={e => setQuery(e.target.value)}
                    value={query}
                    autoComplete="off"
                  />
                  <div className="absolute right-3 top-3 flex items-center gap-1">
                    <kbd style={isDark ? {
                      display: 'inline-flex', alignItems: 'center',
                      border: '1px solid var(--rule-bright)',
                      background: 'var(--surface)',
                      padding: '1px 5px',
                      fontSize: '0.6rem', fontFamily: 'var(--mono)',
                      color: 'var(--text-dim)', borderRadius: '2px',
                    } : {
                      display: 'inline-flex', alignItems: 'center',
                      border: '1px solid #e5e7eb', background: '#f9fafb',
                      padding: '1px 6px', fontSize: '10px',
                      fontWeight: 500, color: '#6b7280', borderRadius: '4px',
                    }}>ESC</kbd>
                  </div>
                </div>

                {allResults.length > 0 && (
                  <Combobox.Options static className="max-h-[28rem] scroll-py-2 overflow-y-auto py-2">
                    {filteredCommands.query.length > 0 && (
                      <Section title="Query" isDark={isDark}>
                        {filteredCommands.query.map(c => <Option key={c.id} command={c} isDark={isDark} />)}
                      </Section>
                    )}
                    {filteredCommands.recent.length > 0 && (
                      <Section title="Recent" isDark={isDark}>
                        {filteredCommands.recent.map(c => <Option key={c.id} command={c} isDark={isDark} />)}
                      </Section>
                    )}
                    {filteredCommands.cycles.length > 0 && (
                      <Section title="Cycles" isDark={isDark}>
                        {filteredCommands.cycles.map(c => <Option key={c.id} command={c} isDark={isDark} />)}
                      </Section>
                    )}
                    {filteredCommands.lists.length > 0 && (
                      <Section title={UI_LABELS.itemPools} isDark={isDark}>
                        {filteredCommands.lists.map(c => <Option key={c.id} command={c} isDark={isDark} />)}
                      </Section>
                    )}
                    {itemResults.length > 0 && (
                      <Section title="Items" isDark={isDark}>
                        {itemResults.map(c => <Option key={c.id} command={c} isDark={isDark} />)}
                      </Section>
                    )}
                    {filteredCommands.navigation.length > 0 && (
                      <Section title="Navigate" isDark={isDark}>
                        {filteredCommands.navigation.map(c => <Option key={c.id} command={c} isDark={isDark} />)}
                      </Section>
                    )}
                    {filteredCommands.tools.length > 0 && (
                      <Section title="Tools" isDark={isDark}>
                        {filteredCommands.tools.map(c => <Option key={c.id} command={c} isDark={isDark} />)}
                      </Section>
                    )}
                    {filteredCommands.actions.length > 0 && (
                      <Section title="Actions" isDark={isDark}>
                        {filteredCommands.actions.map(c => <Option key={c.id} command={c} isDark={isDark} />)}
                      </Section>
                    )}
                  </Combobox.Options>
                )}

                {query && (
                  <div style={{ borderTop: isDark ? '1px solid var(--rule)' : '1px solid #f3f4f6', padding: '4px 8px 8px' }}>
                    <button
                      onClick={() => { navigate(`/prioritization/search?q=${encodeURIComponent(query)}`); onClose(); }}
                      className="flex items-center justify-between w-full px-3 py-2 text-sm transition-colors"
                      style={{
                        color: isDark ? 'var(--text-sec)' : '#4f46e5',
                        borderRadius: isDark ? '2px' : '8px',
                      }}
                      onMouseEnter={e => (e.currentTarget.style.background = isDark ? 'var(--active-tint)' : '#eef2ff')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    >
                      <span>See all results for &ldquo;{query}&rdquo;</span>
                      <ChevronRight className="h-4 w-4" />
                    </button>
                    {/* Where the Ask row would be. Following its link closes the palette. */}
                    {aiNeedsKey && (
                      <div onClick={onClose} className="px-3 pt-1">
                        <AskAiNeedsKey />
                      </div>
                    )}
                  </div>
                )}

                {query !== '' && allResults.length === 0 && (
                  <div className="px-6 py-14 text-center">
                    <Search className="mx-auto h-8 w-8 mb-3" style={{ color: isDark ? 'var(--text-dim)' : '#e5e7eb' }} />
                    <p className="text-sm font-medium" style={{ color: isDark ? 'var(--text-sec)' : '#374151' }}>No results for "{query}"</p>
                    <p className="mt-1 text-xs" style={{ color: isDark ? 'var(--text-ter)' : '#9ca3af' }}>Try a different search term</p>
                  </div>
                )}

                {/* Footer hint bar — keys, so not where there is no keyboard */}
                {showShortcutHints && (
                <div
                  className="flex items-center gap-4 px-4 py-2 text-xs"
                  style={{
                    borderTop: isDark ? '1px solid var(--rule)' : '1px solid #f3f4f6',
                    background: isDark ? 'var(--bg)' : 'rgba(249,250,251,0.8)',
                    color: isDark ? 'var(--text-dim)' : '#9ca3af',
                  }}
                >
                  <span className="flex items-center gap-1">
                    <KbdKey isDark={isDark}>↵</KbdKey> select
                  </span>
                  <span className="flex items-center gap-1">
                    <KbdKey isDark={isDark}>↑</KbdKey>
                    <KbdKey isDark={isDark}>↓</KbdKey> navigate
                  </span>
                  <span className="ml-auto" style={{ fontFamily: isDark ? 'var(--mono)' : 'monospace' }}>⌘K</span>
                </div>
                )}
              </Combobox>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

function KbdKey({ isDark, children }: { isDark: boolean; children: React.ReactNode }) {
  return (
    <kbd style={isDark ? {
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '18px', minWidth: '18px', padding: '0 4px',
      border: '1px solid var(--rule-bright)', background: 'var(--surface)',
      fontFamily: 'var(--mono)', fontSize: '10px',
      color: 'var(--text-dim)', borderRadius: '2px',
    } : {
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '20px', width: '20px',
      border: '1px solid #e5e7eb', background: '#fff',
      fontWeight: 500, fontSize: '12px',
      color: '#6b7280', borderRadius: '4px',
    }}>{children}</kbd>
  );
}

function Section({ title, isDark, children }: { title: string; isDark: boolean; children: React.ReactNode }) {
  return (
    <li>
      <h2
        className="px-4 py-1.5 text-[10px] font-semibold uppercase tracking-widest"
        style={{ color: isDark ? 'var(--text-dim)' : '#9ca3af', fontFamily: isDark ? 'var(--mono)' : undefined }}
      >{title}</h2>
      <ul>{children}</ul>
    </li>
  );
}

function Option({ command, isDark }: { command: Command; isDark: boolean }) {
  const Icon = command.icon || FileText;
  return (
    <Combobox.Option
      value={command}
      className="cursor-pointer select-none px-4 py-2.5"
    >
      {({ active }) => (
        <div
          className="flex items-center gap-3"
          style={{
            background: active ? (isDark ? 'var(--active-tint)' : '#eef2ff') : 'transparent',
            margin: '0 -16px',
            padding: '6px 16px',
          }}
        >
          <div
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center"
            style={{
              background: active
                ? (isDark ? 'var(--active-dim)' : '#c7d2fe')
                : (isDark ? 'var(--surface)' : '#f3f4f6'),
              borderRadius: isDark ? '2px' : '8px',
              border: isDark ? '1px solid var(--rule)' : 'none',
            }}
          >
            <Icon
              className="h-4 w-4"
              style={{ color: active ? (isDark ? 'var(--text-sec)' : '#4f46e5') : (isDark ? 'var(--text-ter)' : '#6b7280') }}
              aria-hidden="true"
            />
          </div>
          <span
            className={`flex-1 text-sm font-medium${command.type === 'query' ? '' : ' truncate'}`}
            style={{ color: active ? (isDark ? 'var(--text-pri)' : '#3730a3') : (isDark ? 'var(--text-sec)' : '#1f2937') }}
          >{command.name}</span>
          {command.description && (
            <span
              // Query rows carry the payload in the description — the English
              // strip, or the reason a translation failed. Truncating that to
              // 140px would hide the thing the row exists to say.
              className={command.type === 'query' ? 'text-xs max-w-[260px] text-right' : 'text-xs truncate max-w-[140px]'}
              style={{ color: active ? (isDark ? 'var(--text-ter)' : '#818cf8') : (isDark ? 'var(--text-dim)' : '#9ca3af') }}
            >{command.description}</span>
          )}
          <ChevronRight
            className="h-3.5 w-3.5 flex-shrink-0"
            style={{ color: active ? (isDark ? 'var(--text-ter)' : '#a5b4fc') : (isDark ? 'var(--rule-bright)' : '#d1d5db') }}
          />
        </div>
      )}
    </Combobox.Option>
  );
}

export default CommandPalette;
